import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { Message } from '@ps/web-core-api';
import { FeatureFlag } from '@ps/web-core-enums';
import { canUseDOM, canUseLocalStorage } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import { get, uniq } from 'lodash';
import type { Selector } from 'reselect';

import { select } from '../../../index';
import { storeSubscribe } from '../../api/storeSubscribe';
import type { EffectParams, Model, RootState, SelectorCreator, Slicer } from '../../types';

type Service = 'cashier' | 'notifications' | 'crm' | 'casino' | 'sports';
const commonServices: Service[] = ['cashier', 'notifications', 'crm'];
const brandServices = new Map<BrandTypes, Service[]>([
  [BrandTypes.STARS_CASINO, ['casino']],
  [BrandTypes.BET_STARS, ['sports']]
]);
interface SystemMessage {
  body: SystemState;
}

type Status = 'green' | 'amber' | 'red';

export interface SystemState {
  [key: string]: Status;
}

const ONE_MINUTE = 60 * 1000;

const reducers = {
  update: (state: SystemState, { body }: SystemMessage): SystemState => {
    const localStorageData = canUseLocalStorage
      ? JSON.parse(localStorage.getItem('status-check') || '{}')
      : {};
    return {
      ...state,
      ...body,
      ...(typeof localStorageData === 'object' ? localStorageData : {})
    };
  }
};

const effects = {
  async check(_: undefined, params: EffectParams) {
    await Promise.all([
      effects.checkCore(undefined, params),
      effects.checkSports(undefined, params)
    ]);
  },
  async checkCore(_: undefined, { dispatch, request }: EffectParams) {
    try {
      dispatch.system.update(
        await request<SystemState>({
          service: '/api/v1-preview/system/status',
          http: true
        })
      );
    } catch {
      // do nothing
    }
  },
  async checkSports(_: undefined, { select, dispatch, request, rootState }: EffectParams) {
    if (!select.feature.check(rootState, FeatureFlag.ISPStatusCheck)) {
      return;
    }

    const isSports = select.cms.isSportsBrand(rootState);

    if (!isSports) {
      return;
    }

    try {
      const currentState = select.system.getStatus(rootState, 'sports');
      const geoState = select.country.getState(rootState);

      // Adding 'x-stars-geo-state' header to prevent BFF requests during SSR
      // Can be removed once WP-3031 is completed
      const response = await request<SystemState>({
        service: '/sports/web/status',
        http: true,
        ...(!canUseDOM && {
          headers: {
            ['x-stars-geo-state']: geoState
          }
        })
      });

      if (canUseDOM && currentState === 'red' && response.body.sports === 'green') {
        const sportsReloaded = localStorage.getItem('sportsReloaded');

        if (!sportsReloaded) {
          localStorage.setItem('sportsReloaded', '1');
          window.location.reload();
        }
      } else {
        if (canUseDOM) {
          localStorage.removeItem('sportsReloaded');
        }
        dispatch.system.update(response);
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch.system.update({ body: { sports: 'red' } });
    }
  },
  async subscribe(_: undefined, effectParams: EffectParams) {
    storeSubscribe<Message<SystemState>>('/topic/system/status', effectParams).subscribe(
      effectParams.dispatch.system.update
    );
    if (select.feature.check(effectParams.rootState, FeatureFlag.ISPStatusCheck)) {
      setInterval(effectParams.dispatch.system.checkSports, ONE_MINUTE);
    }
  }
};

interface SystemModel extends Model<SystemState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const selectors = (slice: Slicer<SystemState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getStatus() {
    return createSelector(
      slice,
      (state: RootState, system: string) => system,
      (systemState, system) => get(systemState, system, 'green') as Status
    );
  },
  isAvailable(this: { getStatus: Selector<RootState, Status, string[]> }) {
    return (state: RootState, _system?: string) =>
      uniq([...commonServices, ...(brandServices.get(select.cms.getBrandId(state)) || [])]).every(
        (system: Service) => this.getStatus(state, system) !== 'red'
      );
  },
  isCMSAvailable(this: { getStatus: Selector<RootState, Status, string[]> }) {
    return (state: RootState, _system?: string) =>
      ![BrandTypes.POKER_STARS, BrandTypes.STARS_CASINO, BrandTypes.BET_STARS].includes(
        select.cms.getBrandId(state)
      ) || this.getStatus(state, 'cms') !== 'red';
  },
  isRAMAvailable(this: { getStatus: Selector<RootState, Status, string[]> }) {
    return (state: RootState, _system?: string) =>
      ['auth', 'account'].every((service) => this.getStatus(state, service) !== 'red');
  }
});

const system: SystemModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default system;
